import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import type { CollectionEntryFragment } from '@spotify/confidence-graphql';

import { useCollectionEntries, useFavoritesCollection } from '../hooks';

type FavoritesContextValue = {
  entryNamesByResource: Map<string, string[]>;
  loading: boolean;
};

const groupEntryNames = (
  entries: readonly CollectionEntryFragment[],
): Map<string, string[]> => {
  const byResource = new Map<string, string[]>();
  entries.forEach(entry => {
    if (!entry.resource || !('name' in entry.resource)) return;
    const names = byResource.get(entry.resource.name) ?? [];
    byResource.set(entry.resource.name, [...names, entry.name]);
  });
  return byResource;
};

const FavoritesContext = createContext<FavoritesContextValue | null>(null);

const NO_FAVORITES: FavoritesContextValue = {
  entryNamesByResource: new Map(),
  loading: false,
};

const LoadedFavoritesProvider = ({ children }: PropsWithChildren) => {
  const { favoritesCollectionName } = useFavoritesCollection();
  const { collectionEntries, isLoading, hasNextPage, nextPage } =
    useCollectionEntries({
      parent: favoritesCollectionName ?? '',
      skip: !favoritesCollectionName,
    });

  useEffect(() => {
    if (hasNextPage && !isLoading) {
      nextPage();
    }
  }, [hasNextPage, isLoading, nextPage]);

  const value = useMemo<FavoritesContextValue>(
    () => ({
      entryNamesByResource: groupEntryNames(collectionEntries ?? []),
      loading: isLoading || hasNextPage,
    }),
    [collectionEntries, isLoading, hasNextPage],
  );

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const FavoritesProvider = ({
  enabled,
  children,
}: PropsWithChildren<{ enabled: boolean }>) =>
  enabled ? (
    <LoadedFavoritesProvider>{children}</LoadedFavoritesProvider>
  ) : (
    <FavoritesContext.Provider value={NO_FAVORITES}>
      {children}
    </FavoritesContext.Provider>
  );

export const useFavorites = (): FavoritesContextValue => {
  const value = useContext(FavoritesContext);
  if (!value) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return value;
};
